import { useState, useEffect, useCallback } from 'react';

export interface OdometerReading {
  id: string;
  reading: number;
  timestamp: Date;
  location?: {
    lat: number;
    lng: number;
  };
  type: 'start_day' | 'store_arrival' | 'store_departure' | 'end_day' | 'manual';
  storeId?: number;
  storeName?: string;
  calculatedReading?: number; // GPS-precalculated value
  adjustment?: number; // Difference from calculated
}

interface OdometerData {
  readings: OdometerReading[];
  currentReading: number | null;
  lastLocation: { lat: number; lng: number } | null;
  totalMiles: number;
}

interface UseOdometerResult {
  odometerData: OdometerData;
  isLoading: boolean;
  error: string | null;
  startDayOdometer: (reading: number, retroactiveTimestamp?: Date) => Promise<void>;
  recordStoreArrival: (storeId: number, storeName: string, reading: number, calculatedReading?: number) => Promise<void>;
  recordStoreDeparture: (storeId: number, storeName: string, reading: number) => Promise<void>;
  endDayOdometer: (reading: number) => Promise<void>;
  calculateExpectedOdometer: (targetLat: number, targetLng: number) => number | null;
  resetOdometer: () => void;
}

const STORAGE_KEY = 'rdm_odometer_tracking';

// Calculate distance between two points in miles (Haversine formula)
function calculateDistanceMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const initialState: OdometerData = {
  readings: [],
  currentReading: null,
  lastLocation: null,
  totalMiles: 0,
};

export function useOdometer(): UseOdometerResult {
  const [odometerData, setOdometerData] = useState<OdometerData>(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert date strings back to Date objects
        const restored: OdometerData = {
          ...parsed,
          readings: parsed.readings.map((r: OdometerReading) => ({
            ...r,
            timestamp: new Date(r.timestamp),
          })),
        };

        // Check if this is from a previous day - reset if so
        if (restored.readings.length > 0) {
          const firstReading = restored.readings[0];
          const today = new Date();
          const readingDate = new Date(firstReading.timestamp);
          if (
            readingDate.getDate() !== today.getDate() ||
            readingDate.getMonth() !== today.getMonth() ||
            readingDate.getFullYear() !== today.getFullYear()
          ) {
            // Previous day's data - reset
            localStorage.removeItem(STORAGE_KEY);
            return;
          }
        }

        setOdometerData(restored);
      }
    } catch (err) {
      console.error('Failed to load odometer data:', err);
    }
  }, []);

  // Save to localStorage whenever data changes
  useEffect(() => {
    if (odometerData.readings.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(odometerData));
    }
  }, [odometerData]);

  // Get current GPS location
  const getCurrentLocation = useCallback((): Promise<{ lat: number; lng: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (err) => {
          reject(new Error(`GPS error: ${err.message}`));
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  }, []);

  // Calculate expected odometer reading based on GPS distance
  const calculateExpectedOdometer = useCallback(
    (targetLat: number, targetLng: number): number | null => {
      if (odometerData.currentReading === null || !odometerData.lastLocation) {
        return null;
      }

      const distance = calculateDistanceMiles(
        odometerData.lastLocation.lat,
        odometerData.lastLocation.lng,
        targetLat,
        targetLng
      );

      // Round to 1 decimal place and add to current reading
      return Math.round((odometerData.currentReading + distance) * 10) / 10;
    },
    [odometerData.currentReading, odometerData.lastLocation]
  );

  // Record the odometer at clock-in.
  // Optional retroactiveTimestamp: for retroactive start when user forgot to start at home.
  //
  // A rep may clock in more than once a day (#3452), so this APPENDS to the
  // day's readings. Miles between a clock-out and the next clock-in are the
  // rep's own, so `totalMiles` carries over untouched rather than absorbing
  // that gap. (A reading from a previous day is already dropped on load.)
  const startDayOdometer = useCallback(
    async (reading: number, retroactiveTimestamp?: Date) => {
      setIsLoading(true);
      setError(null);

      try {
        const location = await getCurrentLocation();
        const timestamp = retroactiveTimestamp || new Date();

        const newReading: OdometerReading = {
          id: generateId(),
          reading,
          timestamp,
          location: retroactiveTimestamp ? undefined : location, // Don't record store GPS as start location
          type: 'start_day',
        };

        setOdometerData((prev) => ({
          readings: [...prev.readings, newReading],
          currentReading: reading,
          lastLocation: retroactiveTimestamp ? null : location,
          totalMiles: prev.totalMiles,
        }));
      } catch (err) {
        // Record without GPS
        const timestamp = retroactiveTimestamp || new Date();
        const newReading: OdometerReading = {
          id: generateId(),
          reading,
          timestamp,
          type: 'start_day',
        };

        setOdometerData((prev) => ({
          readings: [...prev.readings, newReading],
          currentReading: reading,
          lastLocation: null,
          totalMiles: prev.totalMiles,
        }));

        setError(err instanceof Error ? err.message : 'Failed to get location');
      } finally {
        setIsLoading(false);
      }
    },
    [getCurrentLocation]
  );

  // Record odometer at store arrival
  const recordStoreArrival = useCallback(
    async (storeId: number, storeName: string, reading: number, calculatedReading?: number) => {
      setIsLoading(true);
      setError(null);

      try {
        const location = await getCurrentLocation();
        const now = new Date();

        const adjustment = calculatedReading ? reading - calculatedReading : undefined;
        const milesDriven = odometerData.currentReading ? reading - odometerData.currentReading : 0;

        const newReading: OdometerReading = {
          id: generateId(),
          reading,
          timestamp: now,
          location,
          type: 'store_arrival',
          storeId,
          storeName,
          calculatedReading,
          adjustment,
        };

        setOdometerData((prev) => ({
          readings: [...prev.readings, newReading],
          currentReading: reading,
          lastLocation: location,
          totalMiles: prev.totalMiles + Math.max(0, milesDriven),
        }));
      } catch (err) {
        // Record without GPS
        const now = new Date();
        const adjustment = calculatedReading ? reading - calculatedReading : undefined;
        const milesDriven = odometerData.currentReading ? reading - odometerData.currentReading : 0;

        const newReading: OdometerReading = {
          id: generateId(),
          reading,
          timestamp: now,
          type: 'store_arrival',
          storeId,
          storeName,
          calculatedReading,
          adjustment,
        };

        setOdometerData((prev) => ({
          readings: [...prev.readings, newReading],
          currentReading: reading,
          lastLocation: prev.lastLocation,
          totalMiles: prev.totalMiles + Math.max(0, milesDriven),
        }));

        setError(err instanceof Error ? err.message : 'Failed to get location');
      } finally {
        setIsLoading(false);
      }
    },
    [getCurrentLocation, odometerData.currentReading]
  );

  // Record odometer at store departure
  const recordStoreDeparture = useCallback(
    async (storeId: number, storeName: string, reading: number) => {
      setIsLoading(true);
      setError(null);

      try {
        const location = await getCurrentLocation();
        const now = new Date();

        const newReading: OdometerReading = {
          id: generateId(),
          reading,
          timestamp: now,
          location,
          type: 'store_departure',
          storeId,
          storeName,
        };

        setOdometerData((prev) => ({
          ...prev,
          readings: [...prev.readings, newReading],
          currentReading: reading,
          lastLocation: location,
        }));
      } catch (err) {
        // Record without GPS
        const now = new Date();

        const newReading: OdometerReading = {
          id: generateId(),
          reading,
          timestamp: now,
          type: 'store_departure',
          storeId,
          storeName,
        };

        setOdometerData((prev) => ({
          ...prev,
          readings: [...prev.readings, newReading],
          currentReading: reading,
        }));

        setError(err instanceof Error ? err.message : 'Failed to get location');
      } finally {
        setIsLoading(false);
      }
    },
    [getCurrentLocation]
  );

  // Record end of day odometer
  const endDayOdometer = useCallback(
    async (reading: number) => {
      setIsLoading(true);
      setError(null);

      try {
        const location = await getCurrentLocation();
        const now = new Date();

        const milesDriven = odometerData.currentReading ? reading - odometerData.currentReading : 0;

        const newReading: OdometerReading = {
          id: generateId(),
          reading,
          timestamp: now,
          location,
          type: 'end_day',
        };

        setOdometerData((prev) => ({
          ...prev,
          readings: [...prev.readings, newReading],
          currentReading: reading,
          lastLocation: location,
          totalMiles: prev.totalMiles + Math.max(0, milesDriven),
        }));
      } catch (err) {
        // Record without GPS
        const now = new Date();
        const milesDriven = odometerData.currentReading ? reading - odometerData.currentReading : 0;

        const newReading: OdometerReading = {
          id: generateId(),
          reading,
          timestamp: now,
          type: 'end_day',
        };

        setOdometerData((prev) => ({
          ...prev,
          readings: [...prev.readings, newReading],
          currentReading: reading,
          totalMiles: prev.totalMiles + Math.max(0, milesDriven),
        }));

        setError(err instanceof Error ? err.message : 'Failed to get location');
      } finally {
        setIsLoading(false);
      }
    },
    [getCurrentLocation, odometerData.currentReading]
  );

  // Reset odometer data
  const resetOdometer = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setOdometerData(initialState);
    setError(null);
  }, []);

  return {
    odometerData,
    isLoading,
    error,
    startDayOdometer,
    recordStoreArrival,
    recordStoreDeparture,
    endDayOdometer,
    calculateExpectedOdometer,
    resetOdometer,
  };
}
